<template>
  <div class="sections-admin-container">
    <!-- Banner d'information pour les non-admins -->
    <AdminPermissionBanner />

    <!-- En-tête de la page -->
    <div class="bg-white shadow-sm border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-6">
          <div class="flex items-center space-x-4">
            <button
              class="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              @click="$router.push('/admin')">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18">
                </path>
              </svg>
              <span>Retour</span>
            </button>
            <h1 class="text-3xl font-bold text-gray-900">Gestion des Sections</h1>
          </div>

          <!-- Boutons d'action -->
          <div class="flex items-center space-x-3">
            <button
:disabled="sectionsExport.exporting.value"
              class="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              @click="handleExportSections">
              <svg
v-if="!sectionsExport.exporting.value" class="w-5 h-5" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                </path>
              </svg>
              <svg v-else class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path
class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                </path>
              </svg>
              <span v-if="!sectionsExport.exporting.value">Exporter</span>
              <span v-else>Export...</span>
            </button>

            <button
              class="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              @click="showImportModal = true">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
              </svg>
              <span>Importer</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenu principal -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Composant principal de la table des sections -->
      <SectionsTableView
@open-add-root-modal="modalsRef?.openAddRootModal()"
        @open-problems-modal="orphanManagementRef?.openOrphanModal()"
        @open-translation-modal="modalsRef?.openTranslationModal($event)"
        @open-condition-modal="(section, config) => modalsRef?.openConditionModal(section, config)"
        @confirm-delete="modalsRef?.openDeleteModal($event)" @edit-translation="modalsRef?.openTranslationModal($event)"
        @show-notification="showNotification" />

      <!-- Composant des modaux -->
      <SectionModals ref="modalsRef" @show-notification="showNotification" />

      <!-- Composant de gestion des sections à problèmes -->
      <OrphanManagement ref="orphanManagementRef" @show-notification="showNotification" />

      <!-- Modal d'import des sections -->
      <SectionsImportModal
:is-open="showImportModal" @close="showImportModal = false"
        @imported="handleImportCompleted" />
    </div>

    <!-- Système de notifications -->
    <Teleport to="body">
      <div v-if="notification.show" class="section-notification-container">
        <div
class="section-notification" :class="{
          'section-notification-success': notification.type === 'success',
          'section-notification-error': notification.type === 'error',
          'section-notification-warning': notification.type === 'warning',
          'section-notification-info': notification.type === 'info'
        }">
          <div class="section-notification-content">
            <div class="section-notification-icon">
              {{ getNotificationIcon(notification.type) }}
            </div>
            <div class="section-notification-message">
              {{ notification.message }}
            </div>
            <button class="section-notification-close" @click="hideNotification">
              ✕
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import AdminPermissionBanner from '@/components/admin/AdminPermissionBanner.vue';
import OrphanManagement from '@/components/admin/sections/OrphanManagement.vue';
import SectionModals from '@/components/admin/sections/SectionModals.vue';
import SectionsTableView from '@/components/admin/sections/SectionsTableView.vue';
import SectionsImportModal from '@/components/admin/SectionsImportModal.vue';
import { useAdminNotifications } from '@/composables/useAdminPermissions';
import { useSectionsExport } from '@/composables/useSectionsExport';
import { useAuthStore } from '@/stores/auth';
import { useSectionsStore } from '@/stores/sectionsStore';
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue';

export default defineComponent({
  name: 'Sections',
  components: {
    SectionsTableView,
    SectionModals,
    SectionsImportModal,
    OrphanManagement,
    AdminPermissionBanner,
  },
  setup() {
    const sectionsStore = useSectionsStore();
    const authStore = useAuthStore();
    const adminNotifications = useAdminNotifications();
    const sectionsExport = useSectionsExport();

    // Références aux composants enfants
    const modalsRef = ref<InstanceType<typeof SectionModals> | null>(null);
    const orphanManagementRef = ref<InstanceType<typeof OrphanManagement> | null>(null);

    // État pour le modal d'import
    const showImportModal = ref(false);

    // Permissions basées sur le rôle utilisateur (inclut superadmin)
    const isAdmin = computed((): boolean => authStore.isAdmin);

    // Système de notifications
    const notification = ref({
      show: false,
      message: '',
      type: 'info' as 'success' | 'error' | 'warning' | 'info',
      timeout: null as ReturnType<typeof setTimeout> | null
    });

    // Méthodes de notification
    const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): void => {
      // Nettoyer le timeout précédent si il existe
      if (notification.value.timeout) {
        clearTimeout(notification.value.timeout);
      }

      notification.value = {
        show: true,
        message,
        type,
        timeout: null
      };

      // Auto-masquer après 5 secondes (sauf pour les erreurs qui restent plus longtemps)
      const duration = type === 'error' ? 8000 : 5000;
      notification.value.timeout = setTimeout(() => {
        hideNotification();
      }, duration);
    };

    const hideNotification = (): void => {
      if (notification.value.timeout) {
        clearTimeout(notification.value.timeout);
        notification.value.timeout = null;
      }
      notification.value.show = false;
    };

    const getNotificationIcon = (type: string): string => {
      switch (type) {
        case 'success':
          return '✅';
        case 'error':
          return '❌';
        case 'warning':
          return '⚠️';
        case 'info':
        default:
          return 'ℹ️';
      }
    };

    // Fonction pour gérer les erreurs API avec messages localisés
    const handleApiError = (error: unknown, context?: string): { message: string; type: 'success' | 'error' | 'warning' | 'info' } => {
      const { message, type } = adminNotifications.prepareErrorNotification(error, context);
      return {
        message,
        type: type as 'success' | 'error' | 'warning' | 'info'
      };
    };

    // Gestionnaire d'export des sections
    const handleExportSections = async (): Promise<void> => {
      try {
        // Valider les données avant export
        const validation = sectionsExport.validateExportData();
        if (!validation.isValid) {
          showNotification(`Erreurs d'export: ${validation.errors.join(', ')}`, 'error');
          return;
        }

        // Lancer le téléchargement
        await sectionsExport.downloadSectionsExport();

        if (sectionsExport.exportError.value) {
          showNotification(sectionsExport.exportError.value, 'error');
        } else {
          showNotification(
            `Export réussi! ${sectionsStore.sections.length} sections exportées.`,
            'success'
          );
        }
      } catch (error) {
        console.error('❌ Erreur lors de l\'export:', error);
        const { message, type } = handleApiError(error, "exporter les sections");
        showNotification(message, type);
      }
    };

    // Gestionnaire d'import des sections
    const handleImportCompleted = (report: any): void => {
      showImportModal.value = false;

      if (report.success) {
        const message = `Import réussi ! ${report.sections_created} sections créées, ${report.sections_updated} mises à jour`;
        showNotification(message, 'success');

        // Rafraîchir les données des sections
        sectionsStore.fetchSectionsData();
      } else {
        showNotification('Erreur lors de l\'import des sections', 'error');
      }
    };

    // Lifecycle hooks
    onMounted(async () => {
      try {
        // Charger toutes les données nécessaires
        await sectionsStore.fetchSectionsData();

        // Afficher un message de bienvenue si aucune section
        if (sectionsStore.sections.length === 0) {
          showNotification("Aucune section trouvée. Commencez par créer une section racine.", "info");
        } else {
          // Afficher les problèmes détectés s'il y en a
          if (sectionsStore.problemCount > 0) {
            showNotification(
              `${sectionsStore.problemCount} section(s) nécessite(nt) votre attention. Cliquez sur "Réparer" pour les corriger.`,
              "warning"
            );
          }
        }
      } catch (error: unknown) {
        console.error('❌ Erreur lors du chargement des sections:', error);
        const { message, type } = handleApiError(error, "charger les sections");
        showNotification(message, type);
      }
    });

    onUnmounted(() => {
      // Nettoyer les timeouts
      if (notification.value.timeout) {
        clearTimeout(notification.value.timeout);
      }
    });

    return {
      sectionsStore,
      authStore,
      sectionsExport,
      modalsRef,
      orphanManagementRef,
      notification,
      showImportModal,

      // Permissions
      isAdmin,

      // Méthodes de notification
      showNotification,
      hideNotification,
      getNotificationIcon,
      handleApiError,

      // Méthodes d'export/import
      handleExportSections,
      handleImportCompleted,
    };
  }
});
</script>

<style scoped>
/* ===================================
   SECTIONS ADMIN - STYLES PRINCIPAUX
   ================================== */

/* Variables CSS pour la cohérence */
:root {
  --sections-border-color: #e2e8f0;
  --sections-border-radius: 0.375rem;
  --sections-hover-bg: #f3f4f6;
  --sections-transition: all 0.2s ease;
  --sections-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  --sections-tree-line-color: #e5e7eb;
  --sections-tooltip-z: 50;
  --sections-modal-z: 50;
}

/* Styles spécifiques au composant principal */
.sections-admin-container {
  min-height: 100vh;
  background-color: #f9fafb;
}

/* ===================================
   SYSTÈME DE NOTIFICATIONS
   ================================== */

.section-notification-container {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 50;
}

.section-notification {
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-left-width: 4px;
  padding: 1rem;
  margin-bottom: 1rem;
  max-width: 28rem;
  animation: slideInRight 0.3s ease-out;
}

.section-notification-success {
  background-color: #f0fdf4;
  border-left-color: #4ade80;
}

.section-notification-error {
  background-color: #fef2f2;
  border-left-color: #f87171;
}

.section-notification-warning {
  background-color: #fffbeb;
  border-left-color: #facc15;
}

.section-notification-info {
  background-color: #eff6ff;
  border-left-color: #60a5fa;
}

.section-notification-content {
  display: flex;
  align-items: flex-start;
}

.section-notification-icon {
  font-size: 1.125rem;
  line-height: 1.75rem;
  margin-right: 0.75rem;
  margin-top: 0.125rem;
}

.section-notification-message {
  flex: 1 1 0%;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
}

.section-notification-success .section-notification-message {
  color: #166534;
}

.section-notification-error .section-notification-message {
  color: #991b1b;
}

.section-notification-warning .section-notification-message {
  color: #92400e;
}

.section-notification-info .section-notification-message {
  color: #1e40af;
}

.section-notification-close {
  margin-left: 0.75rem;
  color: #9ca3af;
  transition: color 0.2s ease;
  border: none;
  background: none;
  cursor: pointer;
}

.section-notification-close:hover,
.section-notification-close:focus {
  color: #4b5563;
  outline: none;
}

/* Animation d'entrée */
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Animation de sortie */
.section-notification.fade-out {
  animation: slideOutRight 0.3s ease-in forwards;
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }

  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* ===================================
   STYLES GLOBAUX POUR LES COMPOSANTS ENFANTS
   ================================== */

/* Layout principal */
:deep(.sections-header) {
  background-color: white;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid #e5e7eb;
}

:deep(.sections-header-content) {
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
}

:deep(.sections-header-inner) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

:deep(.sections-title) {
  font-size: 1.875rem;
  line-height: 2.25rem;
  font-weight: 700;
  color: #111827;
}

:deep(.sections-actions) {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

:deep(.sections-main-content) {
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding: 2rem 1rem;
}

/* Styles pour les tableaux */
:deep(.sections-table-container) {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

:deep(.sections-table-header) {
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.sections-table-header .section-row) {
  padding: 0.75rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

:deep(.sections-table-body) {
  background-color: white;
}

/* Styles pour les lignes de sections */
:deep(.section-item) {
  border-bottom: 1px solid #f3f4f6;
}

:deep(.section-row) {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  position: relative;
  transition: var(--sections-transition);
  min-height: 3rem;
}

:deep(.section-row:hover) {
  background-color: #f9fafb;
}

:deep(.section-row-invalid-type) {
  background-color: #fed7aa;
}

/* Actions de section */
:deep(.section-actions) {
  width: 6rem;
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

:deep(.section-action-btn) {
  border-radius: 9999px;
  padding: 0.375rem;
  transition: background-color 0.15s ease, color 0.15s ease;
  border: none;
  cursor: pointer;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.section-action-btn-drag) {
  background-color: #f3f4f6;
  color: #4b5563;
}

:deep(.section-action-btn-drag:hover) {
  background-color: #e5e7eb;
  color: #1f2937;
}

:deep(.section-action-btn-add) {
  background-color: #dcfce7;
  color: #059669;
}

:deep(.section-action-btn-add:hover) {
  background-color: #bbf7d0;
  color: #047857;
}

:deep(.section-action-btn-delete) {
  background-color: #fee2e2;
  color: #dc2626;
}

:deep(.section-action-btn-delete:hover) {
  background-color: #fecaca;
  color: #b91c1c;
}

:deep(.section-action-btn-condition) {
  background-color: #f3e8ff;
  color: #9333ea;
}

:deep(.section-action-btn-condition:hover) {
  background-color: #e9d5ff;
  color: #7c3aed;
}

:deep(.section-action-btn-translation) {
  background-color: #dbeafe;
  color: #2563eb;
}

:deep(.section-action-btn-translation:hover) {
  background-color: #bfdbfe;
  color: #1d4ed8;
}

/* Styles pour les boutons d'action */
:deep(.sections-btn) {
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: background-color 0.2s ease, color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  cursor: pointer;
}

:deep(.sections-btn-primary) {
  background-color: #2563eb;
  color: white;
}

:deep(.sections-btn-primary:hover) {
  background-color: #1d4ed8;
}

:deep(.sections-btn-success) {
  background-color: #059669;
  color: white;
}

:deep(.sections-btn-success:hover) {
  background-color: #047857;
}

:deep(.sections-btn-warning) {
  background-color: #f97316;
  color: white;
}

:deep(.sections-btn-warning:hover) {
  background-color: #ea580c;
}

:deep(.sections-btn-danger) {
  background-color: #dc2626;
  color: white;
}

:deep(.sections-btn-danger:hover) {
  background-color: #b91c1c;
}

:deep(.sections-btn-small) {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

/* Animation pour les boutons de réparation */
:deep(.sections-btn-repair-pulse) {
  animation: sections-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes sections-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: .7;
  }
}

/* Styles pour l'affichage hiérarchique */
:deep(.section-hierarchy) {
  display: flex;
  align-items: center;
  flex-grow: 1;
  min-width: 0;
  width: 50%;
}

:deep(.section-indentation) {
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

:deep(.section-tree-lines) {
  color: #9ca3af;
  font-family: monospace;
  font-size: 0.75rem;
  line-height: 1;
  margin-right: 0.25rem;
  white-space: nowrap;
}

:deep(.section-tree-connector) {
  margin-right: 0.125rem;
  display: inline-block;
  width: 0.5rem;
}

:deep(.section-collapse-btn) {
  margin-left: 0.5rem;
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 0.25rem;
  transition: background-color 0.15s ease, color 0.15s ease;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.75rem;
}

:deep(.section-collapse-btn:hover) {
  color: #374151;
  background-color: #f3f4f6;
}

:deep(.section-name-container) {
  flex-grow: 1;
  margin-left: 0.5rem;
}

:deep(.section-name-display) {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: background-color 0.15s ease;
}

:deep(.section-name-display:hover) {
  background-color: #f9fafb;
}

:deep(.section-name-text) {
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 20rem;
}

:deep(.section-name-edit-icon) {
  margin-left: 0.5rem;
  color: #9ca3af;
  opacity: 0;
  transition: opacity 0.2s ease;
  font-size: 0.875rem;
}

:deep(.section-row:hover .section-name-edit-icon) {
  opacity: 1;
}

:deep(.section-name-input) {
  border: 1px solid #d1d5db;
  padding: 0.25rem;
  width: 100%;
  border-radius: 0.25rem;
  font-weight: 600;
}

:deep(.section-name-input:focus) {
  outline: none;
  box-shadow: 0 0 0 2px #3b82f6;
  border-color: #3b82f6;
}

:deep(.section-form-input) {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

:deep(.section-form-input:focus) {
  outline: none;
  box-shadow: 0 0 0 2px #3b82f6;
  border-color: #3b82f6;
}

:deep(.section-form-select) {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

:deep(.section-form-select:focus) {
  outline: none;
  box-shadow: 0 0 0 2px #3b82f6;
  border-color: #3b82f6;
}

:deep(.section-checkbox) {
  width: 1rem;
  height: 1rem;
  color: #2563eb;
  border-radius: 0.25rem;
  cursor: pointer;
}

:deep(.section-checkbox:focus) {
  box-shadow: 0 0 0 2px #3b82f6;
}

/* Colonnes spécialisées */
:deep(.section-conditions-column) {
  width: 8rem;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

:deep(.section-conditions-badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background-color: #f3e8ff;
  color: #7c3aed;
  border-radius: 9999px;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.25rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

:deep(.section-conditions-badge:hover) {
  background-color: #e9d5ff;
}

:deep(.section-translations-column) {
  width: 15rem;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

:deep(.section-flag) {
  cursor: pointer;
  border-radius: 0.25rem;
  transition: transform 0.2s ease;
  width: 1.5em;
  height: 1.5em;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin-right: 0.25rem;
}

:deep(.section-flag:hover) {
  transform: scale(1.1);
}

:deep(.section-type-column) {
  width: 10rem;
  text-align: center;
}

:deep(.section-type-select) {
  border: 1px solid #d1d5db;
  padding: 0.25rem;
  border-radius: 0.25rem;
  width: 100%;
  font-size: 0.875rem;
}

:deep(.section-type-select:focus) {
  outline: none;
  box-shadow: 0 0 0 2px #3b82f6;
  border-color: #3b82f6;
}

:deep(.section-display-column) {
  width: 5rem;
  text-align: center;
}

:deep(.section-mandatory-column) {
  width: 6rem;
  text-align: center;
}

/* Tooltips */
:deep(.section-tooltip) {
  position: absolute;
  background-color: white;
  padding: 0.5rem;
  border-radius: 0.25rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  text-align: left;
  white-space: normal;
  z-index: 100;
  max-width: 300px;
  pointer-events: none;
}

:deep(.section-tooltip-condition) {
  background-color: white;
  min-width: 200px;
  left: 50%;
  transform: translateX(-50%);
  top: 100%;
  z-index: 100;
}

:deep(.section-tooltip-translation) {
  background-color: white;
  min-width: 200px;
  left: 50%;
  transform: translateX(-50%);
  top: 100%;
  z-index: 100;
}

/* Responsive */
@media (max-width: 768px) {
  :deep(.section-name-text) {
    max-width: 8rem;
  }
}

/* Accessibilité */
:deep(.section-focus-visible:focus-visible) {
  outline: none;
  box-shadow: 0 0 0 2px #3b82f6;
  outline-offset: 2px;
}

/* Respect des préférences de mouvement réduit */
@media (prefers-reduced-motion: reduce) {

  :deep(.section-row),
  :deep(.section-collapse-btn) {
    transition: none;
  }

  :deep(.sections-btn-repair-pulse) {
    animation: none;
  }
}
</style>